// React muss im Scope stehen, seit diese Datei JSX rendert (Countdown-Weiche).
// Vorher enthielt sie nur Hooks und kam ohne Default-Import aus.
import React, { useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  getAuthUser,
  getPseudonymization,
} from 'actions/authActions';
import { connect } from 'react-redux';
import PseudonymizationScreen from 'pages/Pseudonymization';
import Header from 'pages/Layout/Header';

export const Authenticate = ({
  getAuthUser, loading, error, children, userId,
}) => {
  const stableGetAuthUser = useCallback(getAuthUser, [userId]);
  useEffect(() => {
    stableGetAuthUser();
  }, [stableGetAuthUser, userId]);

  if (loading) {
    // loading indicator will be displayed (#root:empty)
    return null;
  }
  // handle "not authorized" and "access restricted"
  if (error && ![401, 403].includes(error.status)) {
    return `Failed to get auth user:${JSON.stringify(error)}`;
  }

  return children;
};

Authenticate.propTypes = {
  children: PropTypes.node.isRequired,
  error: PropTypes.shape({ status: PropTypes.number }),
  getAuthUser: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  userId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

Authenticate.defaultProps = {
  error: null,
  userId: null,
};
export const mapStateToProps = ({ currentUser: { loading, error, data } }) => ({
  // initially, before componentDidMount data is null and no errors
  loading: loading || (!data && !error),
  userId: data && data.pk,
  error,
});
const CurrentUserAuthenticate = connect(
  mapStateToProps,
  { getAuthUser },
)(Authenticate);

export const PseudonymizationGate = ({
  children,
  getPseudonymization,
  loading,
  open,
  userId,
}) => {
  // Ausloeser ist ALLEIN die userId: einmal beim Mount (da noch null) und
  // erneut, sobald die Anmeldung den Nutzer bekannt macht — genau dann muss der
  // Loeschstatus neu geholt werden, weil der erste Abruf ohne Token 401 war.
  // ⛔ Der Fehlerstatus des currentUser darf hier NICHT stehen: `errorFor`
  // (shared/lib/callAPI/createReducer.js) nullt den Fehler bei jedem REQUEST
  // und setzt ihn bei FAILURE wieder. Ohne Token pendelt er dauerhaft zwischen
  // null und 401, jedes Kippen feuerte den Abruf erneut, dessen REQUEST den
  // Gate auf `loading` warf und `CurrentUserAuthenticate` samt seinem eigenen
  // Abruf neu montierte — eine Endlosschleife auf dem Login-Bildschirm.
  // Waechter: Authenticate.loop.test.js.
  useEffect(() => {
    getPseudonymization();
  }, [getPseudonymization, userId]);

  if (loading) {
    return null;
  }
  if (open) {
    // Der BESTEHENDE Header (Thomas, 2026-08-09). Er zeigt hier von selbst nur
    // Logo und Namen: sein UserPanel haengt an `isAuthenticated`, und das ist
    // hinter der Sperre false, weil /internship/esuser/ mit 403 antwortet. Kein
    // Sonderpfad noetig -- und kein Klickziel, das ins Leere fuehrt.
    // Der Header steht HIER und nicht im Screen: der Gate ersetzt das ganze
    // Layout, also entscheidet er ueber den Rahmen. Der Screen bleibt dadurch
    // store-frei testbar.
    return (
      <>
        <Header />
        <PseudonymizationScreen />
      </>
    );
  }
  return <CurrentUserAuthenticate>{children}</CurrentUserAuthenticate>;
};

PseudonymizationGate.propTypes = {
  children: PropTypes.node.isRequired,
  getPseudonymization: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  open: PropTypes.bool.isRequired,
  userId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

PseudonymizationGate.defaultProps = {
  userId: null,
};

export const mapPseudonymizationStateToProps = ({
  currentUser,
  pseudonymization: { error, data },
}) => ({
  loading: !data && !error,
  open: !!(data && data.state === 'open'),
  userId: currentUser.data && currentUser.data.pk,
});

export default connect(
  mapPseudonymizationStateToProps,
  { getPseudonymization },
)(PseudonymizationGate);
